import type { ChatFeedStore } from './store.js';

/**
 * Retorna um handler `onKeyDown` para o wrapper do feed.
 *
 * Teclas suportadas:
 *  - ↓ / ↑  → navega selectedIndex (clamp [0, length-1])
 *  - B       → pendingAction ban
 *  - T       → pendingAction timeout
 *  - D       → pendingAction delete
 *
 * Ignora tudo quando event.target é input/textarea (compositor aberto).
 */
export function useChatNavigation(store: ChatFeedStore) {
  function onKeyDown(e: React.KeyboardEvent<HTMLElement>) {
    const tag = (e.target as HTMLElement).tagName.toLowerCase();
    if (tag === 'input' || tag === 'textarea') return;

    const { messages, selectedIndex, setSelectedIndex, setPendingAction } = store.getState();

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (messages.length === 0) return;

      if (selectedIndex === null) {
        // Mod abre o painel querendo agir na msg que acabou de chegar.
        // Tanto ↓ quanto ↑ partindo de null vão pro fim (mais recente).
        setSelectedIndex(messages.length - 1);
        return;
      }

      if (e.key === 'ArrowDown') {
        setSelectedIndex(Math.min(selectedIndex + 1, messages.length - 1));
      } else {
        setSelectedIndex(Math.max(selectedIndex - 1, 0));
      }
      return;
    }

    const key = e.key.toLowerCase();
    if (key !== 'b' && key !== 't' && key !== 'd') return;

    if (selectedIndex === null || selectedIndex < 0 || selectedIndex >= messages.length) return;

    const msg = messages[selectedIndex];
    if (!msg || msg.type !== 'message') return;

    const msgKey = `${msg.platform}:${msg.externalId}`;

    if (key === 'b') {
      e.preventDefault();
      setPendingAction({ kind: 'ban', msgKey });
    } else if (key === 't') {
      e.preventDefault();
      setPendingAction({ kind: 'timeout', msgKey });
    } else if (key === 'd') {
      e.preventDefault();
      setPendingAction({ kind: 'delete', msgKey });
    }
  }

  return { onKeyDown };
}
